import * as React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AnalyticsIdentity } from "./components/AnalyticsIdentity.js";
import { AnalyticsReplayGuard } from "./components/AnalyticsReplayGuard.js";
import { AuthGateProvider } from "./components/AuthGate.js";
import { LeaveGuardProvider } from "./components/LeaveGuard.js";
import { ToastProvider } from "./components/ToastProvider.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { ScrollToTop } from "./components/ScrollToTop.js";
import { StreakCelebrations } from "./hooks/useDailyStreakCelebration.js";
import { Responsive } from "./components/Responsive.js";
import { LoadingSpinner } from "./components/LoadingSpinner.js";
// Eager imports — the prerendered marketing surfaces (/, /a–/e, /about,
// /privacy, /terms, and the test-landing pages) plus the tiny 404 catch-all.
// These must render synchronously so the SSR prerender (renderToString) emits
// real HTML for them. Everything else is code-split below.
import { LandingRoute } from "./routes/LandingRoute.js";
import { AboutRoute } from "./routes/AboutRoute.js";
import { PrivacyRoute } from "./routes/PrivacyRoute.js";
import { TermsRoute } from "./routes/TermsRoute.js";
import { TrustRoute } from "./routes/TrustRoute.js";
import { FundingShortLinkRoute } from "./routes/funding/FundingShortLinkRoute.js";
import { FeaturesRoute } from "./routes/FeaturesRoute.js";
import { CompareRoute } from "./routes/CompareRoute.js";
import { NotFoundRoute } from "./routes/NotFoundRoute.js";
import { TestLandingRoute } from "./routes/testLanding/index.js";
import { RequireStaff } from "./components/RequireStaff.js";
import { queryClient } from "./lib/queryClient.js";

// Non-marketing app routes are code-split so a marketing visitor never
// downloads the game host, diagnostic engine, placement flow, dashboard, or
// auth screens just to read the landing page. Each loads its own chunk on
// demand (gated by the <Suspense> boundary around <Routes>). The route modules
// use named exports, so map each to a default for React.lazy.
const CatalogRoute = React.lazy(() =>
  import("./routes/CatalogRoute.js").then((m) => ({ default: m.CatalogRoute })),
);
const TeachersLandingRoute = React.lazy(() =>
  import("./routes/teachers/TeachersLandingRoute.js").then((m) => ({
    default: m.TeachersLandingRoute,
  })),
);
const TeachersDemoReportRoute = React.lazy(() =>
  import("./routes/teachers/TeachersDemoReportRoute.js").then((m) => ({
    default: m.TeachersDemoReportRoute,
  })),
);
// Classrooms & homework — teacher workspace, student surface, parent surface.
const TeacherSignupRoute = React.lazy(() =>
  import("./routes/teach/TeacherSignupRoute.js").then((m) => ({ default: m.TeacherSignupRoute })),
);
const TeachHomeRoute = React.lazy(() =>
  import("./routes/teach/TeachHomeRoute.js").then((m) => ({ default: m.TeachHomeRoute })),
);
const ClassHomeRoute = React.lazy(() =>
  import("./routes/teach/ClassHomeRoute.js").then((m) => ({ default: m.ClassHomeRoute })),
);
const HomeworkWizardRoute = React.lazy(() =>
  import("./routes/teach/HomeworkWizardRoute.js").then((m) => ({
    default: m.HomeworkWizardRoute,
  })),
);
const HomeworkReportRoute = React.lazy(() =>
  import("./routes/teach/HomeworkReportRoute.js").then((m) => ({
    default: m.HomeworkReportRoute,
  })),
);
const HomeworkGateRoute = React.lazy(() =>
  import("./routes/HomeworkGateRoute.js").then((m) => ({ default: m.HomeworkGateRoute })),
);
const JoinRoute = React.lazy(() =>
  import("./routes/student/JoinRoute.js").then((m) => ({ default: m.JoinRoute })),
);
const StudentHomeRoute = React.lazy(() =>
  import("./routes/student/StudentHomeRoute.js").then((m) => ({ default: m.StudentHomeRoute })),
);
const HomeworkPlayRoute = React.lazy(() =>
  import("./routes/student/HomeworkPlayRoute.js").then((m) => ({
    default: m.HomeworkPlayRoute,
  })),
);
const ParentVerifyRoute = React.lazy(() =>
  import("./routes/family/ParentVerifyRoute.js").then((m) => ({ default: m.ParentVerifyRoute })),
);
const FamilySettingsRoute = React.lazy(() =>
  import("./routes/family/FamilySettingsRoute.js").then((m) => ({
    default: m.FamilySettingsRoute,
  })),
);
const FamilyDashboardRoute = React.lazy(() =>
  import("./routes/family/FamilyDashboardRoute.js").then((m) => ({
    default: m.FamilyDashboardRoute,
  })),
);
const GameDetailRoute = React.lazy(() =>
  import("./routes/GameDetailRoute.js").then((m) => ({ default: m.GameDetailRoute })),
);
const PlansRoute = React.lazy(() =>
  import("./routes/PlansRoute.js").then((m) => ({ default: m.PlansRoute })),
);
const WelcomeRoute = React.lazy(() =>
  import("./routes/WelcomeRoute.js").then((m) => ({ default: m.WelcomeRoute })),
);
const PlayRoute = React.lazy(() =>
  import("./routes/PlayRoute.js").then((m) => ({ default: m.PlayRoute })),
);
// Numeria (Babylon MMORPG) launches standalone — its own scene, no match bridge.
const NumeriaWorldRoute = React.lazy(() =>
  import("./routes/NumeriaWorldRoute.js").then((m) => ({ default: m.NumeriaWorldRoute })),
);
const ResultsRoute = React.lazy(() =>
  import("./routes/ResultsRoute.js").then((m) => ({ default: m.ResultsRoute })),
);
const SigninRoute = React.lazy(() =>
  import("./routes/SigninRoute.js").then((m) => ({ default: m.SigninRoute })),
);
const SignupRoute = React.lazy(() =>
  import("./routes/SignupRoute.js").then((m) => ({ default: m.SignupRoute })),
);
const VerifyRoute = React.lazy(() =>
  import("./routes/VerifyRoute.js").then((m) => ({ default: m.VerifyRoute })),
);
const DashboardRoute = React.lazy(() =>
  import("./routes/DashboardRoute.js").then((m) => ({ default: m.DashboardRoute })),
);
const QuizzesRoute = React.lazy(() =>
  import("./routes/QuizzesRoute.js").then((m) => ({ default: m.QuizzesRoute })),
);
const QuizzesNewRoute = React.lazy(() =>
  import("./routes/QuizzesNewRoute.js").then((m) => ({ default: m.QuizzesNewRoute })),
);
const QuizzesShareRoute = React.lazy(() =>
  import("./routes/QuizzesShareRoute.js").then((m) => ({ default: m.QuizzesShareRoute })),
);
const CanonicalQuizRoute = React.lazy(() =>
  import("./routes/CanonicalQuizRoute.js").then((m) => ({ default: m.CanonicalQuizRoute })),
);
const QuizSubjectHubRoute = React.lazy(() =>
  import("./routes/QuizSubjectHubRoute.js").then((m) => ({ default: m.QuizSubjectHubRoute })),
);
const CommunityRoute = React.lazy(() =>
  import("./routes/CommunityRoute.js").then((m) => ({ default: m.CommunityRoute })),
);
const BlogRoute = React.lazy(() =>
  import("./routes/BlogRoute.js").then((m) => ({ default: m.BlogRoute })),
);
const BlogPostRoute = React.lazy(() =>
  import("./routes/BlogPostRoute.js").then((m) => ({ default: m.BlogPostRoute })),
);
const TestPrepHubRoute = React.lazy(() =>
  import("./routes/testLanding/TestPrepHubRoute.js").then((m) => ({
    default: m.TestPrepHubRoute,
  })),
);
const TestPrepBrowseRoute = React.lazy(() =>
  import("./routes/testLanding/TestPrepBrowseRoute.js").then((m) => ({
    default: m.TestPrepBrowseRoute,
  })),
);
const QuizResultsRoute = React.lazy(() =>
  import("./routes/QuizResultsRoute.js").then((m) => ({ default: m.QuizResultsRoute })),
);
const DiagnosticRoute = React.lazy(() =>
  import("./routes/DiagnosticRoute.js").then((m) => ({ default: m.DiagnosticRoute })),
);
const DiagnosticPlayRoute = React.lazy(() =>
  import("./routes/DiagnosticPlayRoute.js").then((m) => ({ default: m.DiagnosticPlayRoute })),
);
const DiagnosticStartRoute = React.lazy(() =>
  import("./routes/DiagnosticStartRoute.js").then((m) => ({ default: m.DiagnosticStartRoute })),
);
const PlacementRoute = React.lazy(() =>
  import("./routes/PlacementRoute.js").then((m) => ({ default: m.PlacementRoute })),
);
const PlacementPreviewRoute = React.lazy(() =>
  import("./routes/PlacementPreviewRoute.js").then((m) => ({ default: m.PlacementPreviewRoute })),
);
const PlacementDetailRoute = React.lazy(() =>
  import("./routes/PlacementDetailRoute.js").then((m) => ({ default: m.PlacementDetailRoute })),
);
const PlacementSampleRoute = React.lazy(() =>
  import("./routes/PlacementSampleRoute.js").then((m) => ({ default: m.PlacementSampleRoute })),
);
const PlacementExitedRoute = React.lazy(() =>
  import("./routes/PlacementExitedRoute.js").then((m) => ({ default: m.PlacementExitedRoute })),
);
const FeedbackCallRoute = React.lazy(() =>
  import("./routes/FeedbackCallRoute.js").then((m) => ({ default: m.FeedbackCallRoute })),
);
const FundingCheckRoute = React.lazy(() =>
  import("./routes/funding/FundingCheckRoute.js").then((m) => ({
    default: m.FundingCheckRoute,
  })),
);
const FundingBriefRoute = React.lazy(() =>
  import("./routes/funding/FundingBriefRoute.js").then((m) => ({
    default: m.FundingBriefRoute,
  })),
);
const RoomJoinRoute = React.lazy(() =>
  import("./routes/RoomJoinRoute.js").then((m) => ({ default: m.RoomJoinRoute })),
);
const ChallengeRoute = React.lazy(() =>
  import("./routes/ChallengeRoute.js").then((m) => ({ default: m.ChallengeRoute })),
);
const ChallengeResultsRoute = React.lazy(() =>
  import("./routes/ChallengeResultsRoute.js").then((m) => ({ default: m.ChallengeResultsRoute })),
);
const PersonaProfileRoute = React.lazy(() =>
  import("./routes/PersonaProfileRoute.js").then((m) => ({ default: m.PersonaProfileRoute })),
);

// Programmatic SEO practice pages (TEX-2815): index → grade hubs → topic pages.
// Lazy like the other content routes — the prerender still emits full HTML for
// them (entry-server's onAllReady waits for lazy chunks to resolve).
const PracticeIndexRoute = React.lazy(() =>
  import("./routes/practice/PracticeIndexRoute.js").then((m) => ({
    default: m.PracticeIndexRoute,
  })),
);
const PracticeGradeRoute = React.lazy(() =>
  import("./routes/practice/PracticeGradeRoute.js").then((m) => ({
    default: m.PracticeGradeRoute,
  })),
);
const PracticeTopicRoute = React.lazy(() =>
  import("./routes/practice/PracticeTopicRoute.js").then((m) => ({
    default: m.PracticeTopicRoute,
  })),
);
const StandardsHubRoute = React.lazy(() =>
  import("./routes/coverage/StandardsRoutes.js").then((m) => ({
    default: m.StandardsHubRoute,
  })),
);
const StandardsIndexRoute = React.lazy(() =>
  import("./routes/coverage/StandardsRoutes.js").then((m) => ({
    default: m.StandardsIndexRoute,
  })),
);
const StandardDetailRoute = React.lazy(() =>
  import("./routes/coverage/StandardsRoutes.js").then((m) => ({
    default: m.StandardDetailRoute,
  })),
);
const TestCoverageTwoSegmentRoute = React.lazy(() =>
  import("./routes/coverage/TestCoverageRoutes.js").then((m) => ({
    default: m.TestCoverageTwoSegmentRoute,
  })),
);
const TestCoverageThreeSegmentRoute = React.lazy(() =>
  import("./routes/coverage/TestCoverageRoutes.js").then((m) => ({
    default: m.TestCoverageThreeSegmentRoute,
  })),
);

const AdminRoute = React.lazy(() =>
  import("./routes/AdminRoute.js").then((m) => ({ default: m.AdminRoute })),
);

// Full-bleed single-game question preview — the document loaded inside the
// admin Problems "Preview in game" iframe. Bare (no nav/layout) so the game
// owns the whole viewport.
const GamePreviewFrameRoute = React.lazy(() =>
  import("./routes/admin/GamePreviewFrameRoute.js").then((m) => ({
    default: m.GamePreviewFrameRoute,
  })),
);

// Full-page blog post editor (create + edit) — a real content-authoring
// screen, so it lives outside the /admin tab shell like the game preview
// above, rather than cramped into a DataTable-sized tab.
const BlogEditorRoute = React.lazy(() =>
  import("./routes/admin/BlogEditorRoute.js").then((m) => ({ default: m.BlogEditorRoute })),
);

// Devtools only mount in dev — production build tree-shakes the import via
// the import.meta.env.DEV gate below, so no prod bundle cost.
const ReactQueryDevtools = React.lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  })),
);

// Mobile route components — lazy-loaded so a desktop visitor never downloads
// the mobile chunk (and vice versa). Each is gated per-route by <Responsive>
// in the Shell below. Prerendered marketing routes (/, /about, /privacy,
// /terms) are NOT switched here — they reshape via CSS (mobile-field.css) to
// avoid a hydration flash.
const DiagnosticRouteMobile = React.lazy(() =>
  import("./routes/mobile/DiagnosticRouteMobile.js").then((m) => ({
    default: m.DiagnosticRouteMobile,
  })),
);

/** Wrap a lazy mobile element in a Suspense boundary with a Field Day spinner. */
function withSuspense(node: React.ReactNode): React.ReactElement {
  return <React.Suspense fallback={<LoadingSpinner label="Loading…" />}>{node}</React.Suspense>;
}

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AnalyticsIdentity />
        <AnalyticsReplayGuard />
        <ToastProvider>
          <AuthGateProvider>
            <LeaveGuardProvider>
              <ErrorBoundary>
                <Shell />
              </ErrorBoundary>
            </LeaveGuardProvider>
          </AuthGateProvider>
        </ToastProvider>
      </BrowserRouter>
      {import.meta.env.DEV && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      )}
    </QueryClientProvider>
  );
}

export function Shell(): React.ReactElement {
  // The legacy global Nav has been fully retired: every Field Day page
  // (including the persona profile and 404) renders its own brand-stack
  // FieldNav, and game hosts (/play/:slug, /diagnostic/play) intentionally
  // render no nav at all.
  return (
    <>
      <ScrollToTop />
      {/* Mounted once, inside ToastProvider — fires daily-streak milestone
          toasts exactly once across the shared ['me'] cache (TEX-2237). */}
      <StreakCelebrations />
      {/* One Suspense boundary covers every code-split route. The eager
          marketing routes never suspend (so prerender + hydration are
          synchronous); lazy app routes show the spinner while their chunk
          loads. */}
      <React.Suspense fallback={<LoadingSpinner label="Loading…" />}>
        <Routes>
          <Route path="/" element={<LandingRoute galleryVariant="a" />} />
          <Route path="/a" element={<LandingRoute galleryVariant="a" />} />
          <Route path="/b" element={<LandingRoute galleryVariant="b" />} />
          <Route path="/c" element={<LandingRoute galleryVariant="c" />} />
          <Route path="/d" element={<LandingRoute galleryVariant="d" />} />
          <Route path="/e" element={<LandingRoute galleryVariant="e" />} />
          <Route path="/about" element={<AboutRoute />} />
          <Route path="/privacy" element={<PrivacyRoute />} />
          <Route path="/terms" element={<TermsRoute />} />
          <Route path="/trust" element={<TrustRoute />} />
          <Route path="/features" element={<FeaturesRoute />} />
          <Route path="/compare" element={<CompareRoute />} />
          {/* Classrooms & homework — teacher-facing marketing funnel. Static
              routes, so they win over the catch-all /:testSlug; "teachers" is
              also reserved in testLanding/RESERVED_SLUGS. */}
          <Route path="/teachers" element={<TeachersLandingRoute />} />
          <Route path="/teachers/demo-report" element={<TeachersDemoReportRoute />} />
          <Route path="/teach/signup" element={<TeacherSignupRoute />} />
          <Route path="/teach" element={<TeachHomeRoute />} />
          <Route path="/teach/classes/:classId" element={<ClassHomeRoute />} />
          <Route path="/teach/classes/:classId/homework/new" element={<HomeworkWizardRoute />} />
          <Route
            path="/teach/classes/:classId/homework/:assignmentId"
            element={<HomeworkReportRoute />}
          />
          {/* /c exact stays the A/B landing-gallery variant; /c/:code is the
              student class-join deep link. */}
          <Route path="/homework" element={<HomeworkGateRoute />} />
          <Route path="/join" element={<JoinRoute />} />
          <Route path="/c/:code" element={<JoinRoute />} />
          <Route path="/student" element={<StudentHomeRoute />} />
          <Route path="/student/homework/:assignmentId" element={<HomeworkPlayRoute />} />
          <Route path="/family/verify" element={<ParentVerifyRoute />} />
          <Route path="/family/settings" element={<FamilySettingsRoute />} />
          <Route path="/family" element={<FamilyDashboardRoute />} />
          <Route path="/games" element={<CatalogRoute />} />
          <Route path="/games/:slug" element={<GameDetailRoute />} />
          <Route path="/plans" element={<PlansRoute />} />
          <Route path="/welcome" element={<WelcomeRoute />} />
          {/* Feedback-call booking page ($50 gift card campaign, TEX-1997).
              Static route, so it wins over the catch-all /:testSlug; also
              reserved in testLanding/RESERVED_SLUGS. Redirects home when the
              campaign is off (no VITE_FEEDBACK_CALL_BOOKING_URL). */}
          <Route path="/feedback-call" element={<FeedbackCallRoute />} />
          {/* Federal scholarship funding quiz — ad-only landing, noindex, not in
              the nav. Static route (outranks /:testSlug; also reserved in
              testLanding/RESERVED_SLUGS) and a CLIENT_ROUTE_PATTERNS entry in
              middleware-routes.js so the edge serves the shell at 200. */}
          <Route path="/funding-check" element={<FundingCheckRoute />} />
          {/* The article destination — the A/B against the quiz. Same plumbing:
              reserved slug, CLIENT_ROUTE_PATTERNS entry, noindex. */}
          <Route path="/funding-brief" element={<FundingBriefRoute />} />
          {/* /f/:abbr(/:school) — the short link inside the funding texts (a
              160-character budget cannot afford the full re-send URL); redirects
              to /funding-check with the public params. Two routes rather than
              an optional segment so the drift guard can classify each. */}
          <Route path="/f/:abbr" element={<FundingShortLinkRoute />} />
          <Route path="/f/:abbr/:school" element={<FundingShortLinkRoute />} />
          {/* Static path outranks /play/:slug — Numeria bypasses the match shell. */}
          <Route path="/play/numeria" element={<NumeriaWorldRoute />} />
          <Route path="/play/:slug" element={<PlayRoute />} />
          <Route path="/play/:slug/room/:code" element={<RoomJoinRoute />} />
          <Route path="/play/:slug/results" element={<ResultsRoute />} />
          <Route path="/signin" element={<SigninRoute />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/verify" element={<VerifyRoute />} />
          <Route path="/dashboard" element={<DashboardRoute />} />
          {/* Quiz Studio (TEX-2013): "paste text → playable quiz". Open to
              anonymous visitors (TEX-2398): they can make ONE free quiz and play
              it — the signup wall now sits on PLAY (after 5 correct in any quiz,
              see PlayShell), not on creation. Both routes are session-scoped
              (ownedBy/listQuizzes), so a signed-out visitor sees their own session's
              one quiz. /q/:code (share) + /quizzes/results were already public. */}
          <Route path="/quizzes" element={<QuizzesRoute />} />
          <Route path="/quizzes/results" element={<QuizResultsRoute />} />
          <Route path="/quizzes/new" element={<QuizzesNewRoute />} />
          <Route path="/q/:code" element={<QuizzesShareRoute />} />
          {/* Slugged canonical form /q/:code/:slug (SEO + readable links). Same
              component; it canonicalizes the bare /q/:code to this once the quiz
              title is known. The bare form keeps working forever. */}
          <Route path="/q/:code/:slug" element={<QuizzesShareRoute />} />
          <Route path="/quiz/:subjectSlug/:quizSlug" element={<CanonicalQuizRoute />} />
          <Route path="/quiz/:subjectSlug" element={<QuizSubjectHubRoute />} />
          {/* Public community browse page. Static route, so it wins over the
              catch-all /:testSlug below. */}
          <Route path="/community" element={<CommunityRoute />} />
          {/* Blog index + posts. Static routes + reserved slug (see
              testLanding/index.tsx RESERVED_SLUGS), so they win over the
              catch-all /:testSlug below. */}
          <Route path="/blog" element={<BlogRoute />} />
          <Route path="/blog/:slug" element={<BlogPostRoute />} />
          {/* Crawlable hub linking every standardized-test landing page (SEO —
              de-orphans the ~65 /:testSlug pages). Static route + reserved slug,
              so it wins over the catch-all /:testSlug below. */}
          <Route path="/test-prep" element={<TestPrepHubRoute />} />
          {/* Crawlable index of every per-test page — the featured /test-prep
              landing links here so all ~65 pages keep an indexable link. */}
          <Route path="/test-prep/all" element={<TestPrepBrowseRoute />} />
          {/* Programmatic SEO practice pages (TEX-2815). Static /practice
              prefix wins over the catch-all /:testSlug; also reserved in
              testLanding/RESERVED_SLUGS. */}
          <Route path="/practice" element={<PracticeIndexRoute />} />
          <Route path="/practice/:gradeSlug" element={<PracticeGradeRoute />} />
          <Route path="/practice/:gradeSlug/:topicSlug" element={<PracticeTopicRoute />} />
          <Route path="/standards" element={<StandardsIndexRoute />} />
          <Route path="/standards/common-core" element={<StandardsHubRoute />} />
          <Route path="/standards/common-core/:standardSlug" element={<StandardDetailRoute />} />
          <Route
            path="/diagnostic"
            element={
              <Responsive
                desktop={<DiagnosticRoute />}
                mobile={withSuspense(<DiagnosticRouteMobile />)}
              />
            }
          />
          <Route path="/diagnostic/play" element={<DiagnosticPlayRoute />} />
          {/* Signed-in parent/child chooser (TEX-1765): parent → challenge,
              student → solo placement. Signed-out users hit the signup flow. */}
          <Route path="/diagnostic/start" element={<DiagnosticStartRoute />} />
          {/* "Challenge your kid" (TEX-1765): /challenge/:code is the role-aware
              lobby (kid join / parent lobby / sit-out), /results the comparison. */}
          <Route path="/challenge/:code" element={<ChallengeRoute />} />
          <Route path="/challenge/:code/results" element={<ChallengeResultsRoute />} />
          <Route path="/placement" element={<PlacementRoute />} />
          {/* Design harness: open in dev for fast iteration, staff-gated in
              production so the dev dock isn't reachable by every visitor. */}
          <Route
            path="/placement/preview"
            element={
              import.meta.env.DEV ? (
                <PlacementPreviewRoute />
              ) : (
                <RequireStaff>
                  <PlacementPreviewRoute />
                </RequireStaff>
              )
            }
          />
          <Route path="/placement/exited" element={<PlacementExitedRoute />} />
          {/* Public, no-auth marketing sample of a test-tailored report (e.g.
              /placement/sample/sat). Registered before the dynamic
              :placement_id route so it always wins. */}
          <Route path="/placement/sample/:testSlug" element={<PlacementSampleRoute />} />
          <Route path="/placement/:placement_id" element={<PlacementDetailRoute />} />
          <Route
            path="/admin"
            element={<RequireStaff>{withSuspense(<AdminRoute />)}</RequireStaff>}
          />
          <Route
            path="/admin/game-preview"
            element={<RequireStaff>{withSuspense(<GamePreviewFrameRoute />)}</RequireStaff>}
          />
          <Route
            path="/admin/blog/new"
            element={<RequireStaff>{withSuspense(<BlogEditorRoute />)}</RequireStaff>}
          />
          <Route
            path="/admin/blog/:id"
            element={<RequireStaff>{withSuspense(<BlogEditorRoute />)}</RequireStaff>}
          />
          <Route path="/u/:handle" element={<PersonaProfileRoute />} />
          {/* Config-driven standardized-test landing pages. ONE dynamic route
            backs every test (/staar, /map, /sat, /iready + 61 generated).
            Registered LAST so static routes always win; TestLandingRoute also
            holds a RESERVED_SLUGS backstop. Adding a test is just appending a
            config — no edit here. */}
          <Route
            path="/:testSlug/:bandSlug/:domainSlug"
            element={<TestCoverageThreeSegmentRoute />}
          />
          <Route path="/:testSlug/:coverageSlug" element={<TestCoverageTwoSegmentRoute />} />
          <Route path="/:testSlug" element={<TestLandingRoute />} />
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </React.Suspense>
    </>
  );
}
